import * as React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ArrowRight, Clock, Terminal, BookOpen, Gamepad2 } from "lucide-react";
const featuredGuide = {
  title: "The Art of Ambient Layering: A HomeKit Masterclass",
  excerpt: "Discover how to transform your living space using the Moza Method of layered lighting. We explore the technical integration of Nanoleaf, Hue, and Lutron systems to create a seamless, automated atmosphere that adapts to your circadian rhythm.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/homekit_ambient_living_room.png",
  tag: "Featured Review",
  date: "Nov 12, 2023",
  readTime: "12 min read",
  url: "/MozaMethodPage"
};
const recentGuides = [{
  title: "Lutron Caséta vs. Leviton Decora Smart",
  excerpt: "A deep dive into the reliability and response times of the industry's leading smart switches for HomeKit users.",
  tag: "Hardware Review",
  icon: BookOpen,
  date: "Nov 08, 2023",
  url: "/MozaMethodPage"
}, {
  title: "Automating the Perfect Movie Night Scene",
  excerpt: "How to sync your smart blinds and dimmable LEDs using advanced HomeKit shortcuts and occupancy sensors.",
  tag: "Automation Recipes",
  icon: Terminal,
  date: "Nov 05, 2023",
  url: "/AutomationRecipesPage"
}, {
  title: "Philips Hue Gradient Lightstrip Review",
  excerpt: "Testing the color accuracy and brightness levels of the latest entertainment-focused lighting from Signify.",
  tag: "Product Review",
  icon: Gamepad2,
  date: "Nov 01, 2023",
  url: "/MozaMethodPage"
}];
export default function LatestGuides() {
  return <section data-section-id="235698" id="lighting-reviews" className="bg-background text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-[1200px] px-6">
        
        {}
        <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h2 className="font-default text-3xl font-bold tracking-tight text-foreground md:text-5xl">
              Lighting Reviews
            </h2>
            <p className="mt-2 text-muted-foreground font-default">
              Expert analysis of the latest HomeKit-enabled smart lighting solutions.
            </p>
          </div>
          <Button variant="outline" asChild className="shrink-0 rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <Link to="/MozaMethodPage">
              View All Reviews <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          
          {}
          <div className="flex flex-col gap-4 lg:col-span-5">
            {recentGuides.map((guide, i) => {
            const Icon = guide.icon;
            return <Link key={i} data-index={i} to={guide.url} className="group flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary hover:shadow-xl">
                  <div className="flex items-center gap-3 text-xs font-medium text-muted-foreground">
                    <span className="flex items-center gap-1.5 text-primary font-default uppercase tracking-widest">
                      <Icon className="h-3.5 w-3.5" />
                      {guide.tag}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {guide.date}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-default text-lg font-semibold leading-tight text-foreground transition-colors group-hover:text-primary">
                      {guide.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground font-default">
                      {guide.excerpt}
                    </p>
                  </div>
                </Link>;
          })}
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="group h-full overflow-hidden border-border rounded-3xl transition-all hover:border-primary hover:shadow-2xl">
              <Link to={featuredGuide.url} className="flex h-full flex-col">
                <div className="relative overflow-hidden bg-muted">
                  <AspectRatio ratio={16 / 9}>
                    <img src={featuredGuide.image} alt={featuredGuide.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </AspectRatio>
                  <div className="absolute left-4 top-4 rounded-full bg-primary px-4 py-1.5 text-[10px] font-bold tracking-widest text-primary-foreground backdrop-blur-sm">
                    FEATURED
                  </div>
                </div>
                
                <CardContent className="flex flex-1 flex-col justify-center p-6 md:p-8">
                  <div className="mb-4 flex flex-wrap items-center gap-3 text-sm font-medium text-muted-foreground">
                    <span className="rounded-full bg-secondary px-4 py-1 text-secondary-foreground font-default text-xs">
                      {featuredGuide.tag}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {featuredGuide.date}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="hidden sm:inline font-default">{featuredGuide.readTime}</span>
                  </div>
                  
                  <h3 className="mb-4 font-default text-2xl font-bold leading-tight text-foreground transition-colors group-hover:text-primary md:text-4xl">
                    {featuredGuide.title}
                  </h3>
                  
                  <p className="mb-6 text-base text-muted-foreground font-default md:text-lg leading-relaxed">
                    {featuredGuide.excerpt}
                  </p>
                  
                  <div className="mt-auto flex items-center font-bold text-primary uppercase tracking-widest text-xs">
                    Read Full Review 
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </CardContent>
              </Link>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}